import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateSubContractorMutation, useUpdateSubContractorMutation } from '../../../../hooks/mutations/useSubContractorMutation';
import { SubContractor } from '../../../../services/apiSLTP/materials/subContractors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    subContractor: SubContractor | null;
    subContractors: SubContractor[];
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateSubContractorModal: React.FC<ModalProps> = ({
    subContractor,
    isEditMode = true,
    showEditModal,
    setShowEditModal,
    refetch,
    subContractors,
}) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(subContractor?.designation || '');
    const [codeProC, setCodeProC] = useState(subContractor?.codeProC || '');
    const [email, setEmail] = useState(subContractor?.email || '');
    const [isActive, setIsActive] = useState(subContractor?.crudStatus ? subContractor?.crudStatus === crudStatusEnum.OK : true);
    const [displayOrder, setDisplayOrder] = useState(typeof subContractor?.displayOrder === 'number' ? subContractor?.displayOrder.toString() : '');

    const { mutate: mutateSubContractor } = useUpdateSubContractorMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating subContractor:', error.message),
    });

    const { mutate: addSubContractor } = useCreateSubContractorMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding subContractor:', error.message),
    });

    const isFormValid = () => designation.length > 0 && codeProC.length > 0 && verifyUniqueCodeProC();

    const verifyUniqueCodeProC = () => {
        return !subContractors.some((contractor) => contractor.codeProC === codeProC && contractor._id !== subContractor?._id);
    };

    const handleActiveChange = (checked: boolean) => setIsActive(checked);

    const handleSubmit = () => {
        const subContractorData = {
            designation,
            codeProC,
            crudStatus: isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE,
            email,
            displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
        };

        if (isEditMode) {
            mutateSubContractor({
                subContractorId: subContractor?._id || '',
                data: subContractorData,
            });
        } else {
            addSubContractor(subContractorData);
        }
    };

    return (
        <ModalBody
            title={t('subContractor')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isFormValid()}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} errorText={t('error')} required />
            <CustomTextInput
                value={codeProC}
                label={t('codeProC')}
                onChange={setCodeProC}
                errorText={verifyUniqueCodeProC() ? '' : t('codeProCAlreadyUsed')}
                required
            />
            <ToggleLine checked={isActive} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={email} label={t('email')} onChange={setEmail} errorText={t('error')} />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateSubContractorModal;
